import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, MapPin, Flame, ChevronUp, ChevronDown, Minus, Crown } from 'lucide-react';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxTonightInCity from './ArenaxTonightInCity';
import ArenaxCityPulseTicker from './ArenaxCityPulseTicker';

/**
 * Phase 2K PR 4 — City Rankings page.
 *
 * Cultural composite leaderboard.  Each row carries: rank, composite, form
 * chips (🔥 / 👀 / ⚔️), and a one-line tagline.  NOT a stats dashboard — every
 * surface here speaks the language of community significance, not analytics.
 *
 * See narrative_style_guide.md.
 */
export default function ArenaxRankings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('Bengaluru');
  const [sport, setSport] = useState('Football');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (sport) params.set('sport', sport);
    api.get(`/arenax/rankings?${params.toString()}`)
      .then(r => setRows(r.data?.rankings || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [city, sport]);

  const cityOptions = ['Bengaluru', 'Mumbai', 'Delhi', 'Pune', 'Chennai'];
  const sportOptions = ['Football', 'Cricket', 'Badminton', 'Tennis'];

  return (
    <ArenaxLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-rankings-page">
        {/* Hero */}
        <div className="rounded-2xl border border-amber-500/15 p-6 lg:p-8 mb-6"
          style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.07), rgba(244,63,94,0.05) 50%, var(--ax-card-bg-soft))' }}>
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">
              City Rankings
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-slate-100 leading-tight mb-3">
            <span className="arenax-hero-gradient-text">
              Top {sport} Squads — {city}
            </span>
          </h1>
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
            Ranked by city significance — performance, championships, recurring presence, rivalry record,
            attendance. Climb it.
          </p>
        </div>

        {/* Phase 128 — Live in the City ambient ticker (booking-driven civic motion) */}
        <section className="mb-4" data-testid="arenax-city-pulse-section">
          <ArenaxCityPulseTicker city={city} maxItems={8} />
        </section>

        {/* Tonight in the City — narrative ticker above the leaderboard */}
        <section className="mb-6" data-testid="arenax-tonight-section">
          <ArenaxTonightInCity city={city} sport={sport} variant="strip" />
        </section>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="flex items-center gap-1.5" data-testid="arenax-rankings-city-filters">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1.5">City</span>
            {cityOptions.map(c => (
              <button key={c} onClick={() => setCity(c)}
                className={`arenax-chip text-[10px] uppercase tracking-wider ${city === c ? 'is-active' : ''}`}
                data-testid={`arenax-rankings-city-${c.toLowerCase()}`}>
                <MapPin className="h-3 w-3 inline -mt-0.5 mr-1" />
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5" data-testid="arenax-rankings-sport-filters">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1.5">Sport</span>
            {sportOptions.map(s => (
              <button key={s} onClick={() => setSport(s)}
                className={`arenax-chip text-[10px] uppercase tracking-wider ${sport === s ? 'is-active' : ''}`}
                data-testid={`arenax-rankings-sport-${s.toLowerCase()}`}>
                <Flame className="h-3 w-3 inline -mt-0.5 mr-1" />
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        {loading ? (
          <div className="space-y-3" data-testid="arenax-rankings-loading">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 rounded-2xl border border-slate-800 animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 px-6 rounded-2xl border border-dashed border-slate-700/60"
            data-testid="arenax-rankings-empty">
            <Trophy className="mx-auto h-8 w-8 text-slate-500 mb-3" />
            <h3 className="text-lg font-black text-slate-100 mb-1">No squads ranked here yet.</h3>
            <p className="text-sm text-slate-400">Once a few squads play, the city standings start writing themselves.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="arenax-rankings-grid">
            {rows.map(row => <RankingRow key={row.squad_id} row={row} />)}
          </div>
        )}
      </div>
    </ArenaxLayout>
  );
}

function RankingRow({ row }) {
  const isTop = row.rank === 1;
  const climbing = row.rank_climb > 0;
  const falling = row.rank_climb < 0;
  return (
    <Link to={`/arenax/squads/${row.squad_id}`}
      data-testid={`arenax-rankings-row-${row.squad_id}`}
      className="block rounded-2xl border p-4 transition-all hover:translate-y-[-1px]"
      style={{
        borderColor: isTop ? 'rgba(245,158,11,0.45)' : 'rgba(30,41,59,1)',
        background: isTop
          ? 'linear-gradient(135deg, rgba(245,158,11,0.10), var(--ax-card-bg-soft))'
          : 'linear-gradient(135deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))',
      }}>
      <div className="flex items-start gap-4">
        {/* Rank */}
        <div className="shrink-0 w-12 text-center">
          <div className={`text-3xl font-black tabular-nums ${isTop ? 'text-amber-300' : 'text-slate-200'}`}>
            {row.rank}
          </div>
          {row.previous_rank != null && row.rank_climb !== 0 && (
            <div className={`text-[10px] font-black tabular-nums mt-0.5 inline-flex items-center gap-0.5 ${
              climbing ? 'text-emerald-300' : 'text-rose-300'
            }`} data-testid={`arenax-rankings-delta-${row.squad_id}`}>
              {climbing ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {Math.abs(row.rank_climb)}
            </div>
          )}
          {row.previous_rank != null && row.rank_climb === 0 && (
            <Minus className="h-3 w-3 text-slate-600 mx-auto mt-0.5" />
          )}
        </div>
        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isTop && <Crown className="h-3.5 w-3.5 text-amber-300 shrink-0" />}
            <h3 className={`text-lg font-black leading-tight truncate ${isTop ? 'text-amber-100' : 'text-slate-100'}`}>
              {row.squad_name}
            </h3>
          </div>
          {row.tagline && (
            <p className={`text-xs italic leading-snug ${isTop ? 'text-amber-200/85' : 'text-slate-400'}`}
              data-testid={`arenax-rankings-tagline-${row.squad_id}`}>
              {row.tagline}
            </p>
          )}
          {row.form_chips && row.form_chips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`arenax-rankings-form-${row.squad_id}`}>
              {row.form_chips.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide text-amber-200 bg-amber-500/10 border border-amber-500/25">
                  <span>{c.emoji}</span>
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Composite */}
        <div className="shrink-0 text-right">
          <div className={`text-2xl font-black tabular-nums ${isTop ? 'text-amber-300' : 'text-cyan-200'}`}>
            {Math.round(row.composite)}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">
            significance
          </div>
        </div>
      </div>
    </Link>
  );
}
